import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query'; //-tech-debt-react-query;
import { HbrSpinner, HbrTab, HbrTabGroup } from '@harbor/elements-react-standalone';
import { useParams } from 'react-router-dom';
import PartnerComponent from './Partner';
import { useFetchPartner } from '../../hooks/mssp-api-hooks';
import PartnerServiceCreation from './PartnerServiceCreation';
import ServiceCreationHeatMap from './ServiceCreationHeatMap';
import { deleteItem } from '../../support/queries';
import StbButton from '../security-toolbox-ui/StbButton';
import StbApiModal from '../security-toolbox-ui/StbApiModal';
import { CacheKey } from '../../types/cache-keys';


const PartnerDetail: React.FC = () => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  
  const [activeTab, setActiveTab] = useState(0);
  const [visibleComponent, setVisibleComponent] = useState<React.ReactNode>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const navigate = useNavigate();

  const { data: partner, isLoading } = useFetchPartner(Number(id));

  // Check if the partner has only `id` and `name` attributes
  useEffect(() => {
    if (partner && Object.keys(partner).length === 6 && partner.id && partner.name && partner.managed_resale) {
      setActiveTab(1); // Switch to the "Partner Details" tab
      setIsEditing(true); // Enable Edit Mode for PartnerComponent
    }
  }, [partner]);

  useEffect(() => {
    switch (activeTab) {
      case 0:
        setVisibleComponent(<ServiceCreationHeatMap refreshTrigger={refreshTrigger} />);
        break;
      case 1:
        setVisibleComponent(<PartnerComponent isEditing={isEditing} />); // Pass `isEditing` to PartnerComponent
        break;
      case 2:
        setVisibleComponent(
          <PartnerServiceCreation setRefreshTrigger={setRefreshTrigger} />
        );
        break;
      default:
        setVisibleComponent(null);
    }
  }, [activeTab, refreshTrigger, isEditing]); // Add `isEditing` as a dependency

  const handleTabChange = (event: React.ChangeEvent<any>, newValue: number) => {
    setActiveTab(newValue);
    if (newValue !== 1) {
      setIsEditing(false); // Disable Edit Mode when leaving the "Partner Details" tab
    }
  };

  const handleDeletePartnerClick = () => {
    setDeleteModalVisible(true); // Show the delete confirmation modal
  };

  const handleDeleteConfirm = async () => {
    await deleteItem('db/partner/' + id);
    setDeleteSuccess(true);
  };
  
  useEffect(() => {
    if (!deleteModalVisible && deleteSuccess) {
      queryClient.invalidateQueries({ queryKey: [CacheKey.AllPartner] });
      setDeleteSuccess(false);
      navigate(`/mssp`);
    }
  }, [deleteModalVisible, deleteSuccess, queryClient, navigate]);

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false); // Close the modal without taking any action
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="hbr-css__container-page">
      {partner?.name ? (
        <div className="hbr-css__page-title-container hbr-css__layout-col-2xs">
          <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '.5rem' }}>
            <div className="hbr-css__layout-row-sm">
              <div className="hbr-css__text-heading-xl-bold">{partner?.name}</div>
            </div>
            <StbButton role="delete" onClick={handleDeletePartnerClick} className="float-right" />
          </div>
          <HbrTabGroup>
            <HbrTab
              slot="nav"
              active={activeTab === 0}
              onHbr-click={() => handleTabChange(null, 0)}
            >
              Heatmap
            </HbrTab>
            <HbrTab slot="nav" key={1} onClick={() => handleTabChange(null, 1)}>
              Partner Details
            </HbrTab>
            <HbrTab slot="nav" key={2} onClick={() => handleTabChange(null, 2)}>
              Products
            </HbrTab>
          </HbrTabGroup>
        </div>
      ) : (
        <HbrSpinner />
      )}
      {visibleComponent}

      {/* Delete Confirmation Modal */}
      <StbApiModal
        title="Delete Partner"
        open={deleteModalVisible}
        role="delete"
        onClose={async () => handleDeleteCancel()}
        onSubmit={handleDeleteConfirm}
        submitSuccess="The operation completed successfully"
        submitError="The operation could not be completed. Please try again."
      >
        <div>Are you sure you want to delete partner: <em>{partner?.name}</em>?</div>
      </StbApiModal>
    </div>
  );
};

export default PartnerDetail;